/**
 * Check metadata - Essential definitions
 */

export interface CheckMetadata {
  id: string;
  title: string;
  description: string;
  whyThisTest: string;
  acceptedValues?: string[] | { [key: string]: string[] };
  profileSpecific?: {
    iab?: { severity: 'fail' | 'warn' | 'info', threshold?: any };
    google?: { severity: 'fail' | 'warn' | 'info', threshold?: any };
    facebook?: { severity: 'fail' | 'warn' | 'info', threshold?: any };
  };
  remediation?: string;
  learnMoreUrl?: string;
}

const DEFAULT: CheckMetadata = {
  id: '',
  title: '',
  description: 'Validates ad compliance with platform requirements.',
  whyThisTest: 'Ensures creative meets industry standards for delivery.',
  remediation: 'Review validation findings and address issues.'
};

export const CHECK_METADATA: Record<string, CheckMetadata> = new Proxy({
  'dimensions': {
    id: 'dimensions',
    title: 'Dimensions',
    description: 'Measures creative width and height in pixels.',
    whyThisTest: 'Standard sizes ensure proper ad slot fit and platform acceptance.',
    acceptedValues: { 'IAB': ['160x600', '300x250', '728x90', '320x50'], 'Google': ['250x250'], 'Meta': ['1080x1080', '1080x1920'] },
    remediation: 'Use IAB standard: 160x600, 300x250, 728x90, or 320x50'
  },
  'index-file': { ...DEFAULT, id: 'index-file', title: 'Index File', description: 'Verifies index.html at root.', whyThisTest: 'Required entry point for ad servers.', remediation: 'Add index.html at ZIP root.' },
  'backup-ad': { ...DEFAULT, id: 'backup-ad', title: 'Backup Ad', description: 'Checks for backup.html or image.', whyThisTest: 'Fallback when HTML5 fails.', remediation: 'Add backup.html or backup.jpg' },
  'heavy-ad': { ...DEFAULT, id: 'heavy-ad', title: 'Heavy Ad', description: 'Chrome intervention risk.', whyThisTest: 'Prevents removal (4MB, 60s CPU).', remediation: 'Optimize assets, reduce CPU.' },
  'ssl-compatible': { ...DEFAULT, id: 'ssl-compatible', title: 'SSL Compatible', description: 'All requests via HTTPS.', whyThisTest: 'HTTP blocked on HTTPS pages.', remediation: 'Convert HTTP to HTTPS.' },
  'click-through': { ...DEFAULT, id: 'click-through', title: 'Click Through', description: 'clickTag implementation.', whyThisTest: 'Enables tracking and URLs.', remediation: 'Add clickTag variable.' },
  'load-initial': { ...DEFAULT, id: 'load-initial', title: 'Load Size: Initial', description: 'Bytes before DOMContentLoaded.', whyThisTest: 'Impacts render time.', remediation: 'Keep under 200 KB.' },
  'css-minified': { ...DEFAULT, id: 'css-minified', title: 'CSS/JS Minified', description: 'Code minification check.', whyThisTest: 'Reduces file size 30-60%.', remediation: 'Minify with Terser/cssnano.' },
  'images-optimized': { ...DEFAULT, id: 'images-optimized', title: 'Images Optimized', description: 'Image compression check.', whyThisTest: 'Reduces size 50-70%.', remediation: 'Use TinyPNG or ImageOptim.' }
}, {
  get: (target: any, prop: string) => target[prop] || { ...DEFAULT, id: prop, title: prop.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }
}) as Record<string, CheckMetadata>;
